import {
  Box,
  Grid,
  SvgIcon,
  SvgIconTypeMap,
  Theme,
  useTheme,
} from "@material-ui/core";
import React, { memo } from "react";

import ChevronRightOutlinedIcon from "@material-ui/icons/ChevronRight";
import { OverridableComponent } from "@material-ui/core/OverridableComponent";
import styled from "styled-components";

interface PathCellProps {
  cell: {
    value: {
      pathList: string[];
      getTileColor: (theme: Theme, pathName: string) => string;
      badgeWidth?: number;
      linkSymbol?: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
      includeOrder?: boolean;
    };
  };
}

const CustomBadge = styled.div<{
  $bgColor: string;
  $badgeWidth?: number;
}>`
  position: relative;
  display: block;
  padding: 6px;
  width: ${({ $badgeWidth }) =>
    $badgeWidth ? `${$badgeWidth}px` : "max-content"};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: center;
  border-radius: 5px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({ $bgColor }) => $bgColor};
    opacity: 0.15;
  }

  & > span {
    position: relative;
    z-index: 2;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ $bgColor }) => $bgColor};
  }
`;

const StyledSvgIcon = styled(SvgIcon)`
  width: 1.25rem;
  height: 1.25rem;
  fill: ${({ theme }) => theme.palette.text.primary};
  margin: 0 -0.25rem;
`;

export const PathCell = memo<PathCellProps>(({ cell }) => {
  const theme = useTheme();
  const path = cell.value.pathList;
  const getTileColor = cell.value.getTileColor;
  const badgeWidth = cell.value.badgeWidth;
  const linkSymbol = cell.value.linkSymbol ?? ChevronRightOutlinedIcon;
  const includeOrder = cell.value.includeOrder;
  return (
    <Box py={1}>
      <Grid
        container
        spacing={1}
        justifyContent="flex-start"
        alignItems="center"
      >
        {path.map((pathName, index) => {
          const color = getTileColor(theme, pathName);
          return (
            <Grid item key={index}>
              <Grid
                container
                spacing={1}
                justifyContent="flex-start"
                alignItems="center"
                wrap="nowrap"
              >
                <Grid item>
                  <CustomBadge $bgColor={color} $badgeWidth={badgeWidth}>
                    <span>
                      {includeOrder ? `${index + 1}. ${pathName}` : pathName}
                    </span>
                  </CustomBadge>
                </Grid>
                {index !== path.length - 1 ? (
                  <Grid
                    item
                    container
                    justifyContent="center"
                    alignItems="center"
                  >
                    <StyledSvgIcon as={linkSymbol} />
                  </Grid>
                ) : undefined}
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
});
